import React, { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { getBusinessUrl } from '../../lib/domains';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { BriefcaseBusiness, Copy, ExternalLink, Globe, MapPin, Phone, Plus, Share2, Store, LayoutTemplate } from 'lucide-react';

const DEFAULT_FORM = {
  business_name: '',
  username: '',
  logo: '',
  cover_image: '',
  about: '',
  services: '',
  phone: '',
  whatsapp: '',
  email: '',
  location: '',
  opening_hours: '',
  website: '',
  store_link: '',
  vcard_link: '',
  payment_link: '',
  status: 'active',
  seo_title: '',
  seo_description: '',
  seo_keywords: '',
  social_share_image: '',
};

function joinServices(value) {
  if (Array.isArray(value)) return value.join(', ');
  return String(value || '');
}

export default function BusinessProfilePage() {
  const [access, setAccess] = useState({});
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const publicUrl = useMemo(() => {
    const username = form.username || profile?.username || '';
    return username ? getBusinessUrl(`/${username}`) : '';
  }, [form.username, profile?.username]);

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/dashboard/business-profile');
      setAccess(res.data?.access || {});
      const item = res.data?.profile || null;
      setProfile(item);
      setForm(item ? {
        business_name: item.business_name || '',
        username: item.username || '',
        logo: item.logo || '',
        cover_image: item.cover_image || '',
        about: item.about || '',
        services: joinServices(item.services),
        phone: item.phone || '',
        whatsapp: item.whatsapp || '',
        email: item.email || '',
        location: item.location || '',
        opening_hours: item.opening_hours || '',
        website: item.website || '',
        store_link: item.store_link || '',
        vcard_link: item.vcard_link || '',
        payment_link: item.payment_link || '',
        status: item.status || 'active',
        seo_title: item.seo_title || '',
        seo_description: item.seo_description || '',
        seo_keywords: joinServices(item.seo_keywords),
        social_share_image: item.social_share_image || '',
      } : DEFAULT_FORM);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load business profile');
      setProfile(null);
      setForm(DEFAULT_FORM);
      setAccess({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!access?.can_use_business_profile) {
      toast.error('Upgrade your package to use business profile pages.');
      return;
    }
    if (!form.username.trim()) {
      toast.error('Username is required');
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      username: form.username.toLowerCase().trim(),
      services: form.services,
      seo_keywords: form.seo_keywords,
    };
    try {
      const res = profile?.id
        ? await api.put(`/dashboard/business-profile/${profile.id}`, payload)
        : await api.post('/dashboard/business-profile', payload);
      toast.success(res.data?.message || 'Business profile saved');
      setProfile(res.data?.profile || null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save business profile');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async () => {
    if (!profile?.id) return;
    const next = profile.status === 'active' ? 'disabled' : 'active';
    try {
      const res = await api.patch(`/dashboard/business-profile/${profile.id}/status`, { status: next });
      toast.success(res.data?.message || 'Business profile updated');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update business profile');
    }
  };

  const copyShareLink = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success('Business link copied');
      setTimeout(() => setCopied(false), 1200);
    } catch {
      toast.error('Could not copy link');
    }
  };

  const openPublic = () => {
    if (publicUrl) window.open(publicUrl, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return <div className="flex items-center justify-center h-48"><div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Business Profile</h1>
          <p className="text-slate-500 text-sm mt-0.5">Create a public business page with contacts, services and quick action buttons.</p>
        </div>
        <div className="flex items-center gap-2">
          {publicUrl ? (
            <>
              <Button type="button" variant="outline" className="border-slate-200" onClick={copyShareLink}>
                <Copy size={16} className="mr-1" /> {copied ? 'Copied' : 'Copy link'}
              </Button>
              <Button type="button" className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={openPublic}>
                <ExternalLink size={16} className="mr-1" /> Open page
              </Button>
            </>
          ) : null}
        </div>
      </div>

      {!access?.can_use_business_profile ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          Upgrade your package to enable business profile pages.
        </div>
      ) : null}

      <div className="grid gap-4 xl:grid-cols-[1.15fr_0.85fr]">
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <BriefcaseBusiness size={18} /> Profile Setup
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={save} className="grid gap-4 md:grid-cols-2">
              {[
                ['Business Name', 'business_name', 'text'],
                ['Username', 'username', 'text'],
                ['Logo URL', 'logo', 'text'],
                ['Cover Image URL', 'cover_image', 'text'],
                ['Phone', 'phone', 'text'],
                ['WhatsApp', 'whatsapp', 'text'],
                ['Email', 'email', 'email'],
                ['Location', 'location', 'text'],
                ['Opening Hours', 'opening_hours', 'text'],
                ['Website', 'website', 'text'],
                ['Store Link', 'store_link', 'text'],
                ['vCard Link', 'vcard_link', 'text'],
                ['Payment Link', 'payment_link', 'text'],
                ['SEO Title', 'seo_title', 'text'],
                ['Social Share Image', 'social_share_image', 'text'],
              ].map(([label, field, type]) => (
                <div key={field}>
                  <Label>{label}</Label>
                  <Input
                    type={type}
                    value={form[field]}
                    onChange={(e) => update(field, field === 'username' ? e.target.value.toLowerCase().replace(/\s+/g, '-') : e.target.value)}
                    className="mt-1"
                    placeholder={field === 'username' ? 'dancano' : ''}
                  />
                </div>
              ))}

              <div className="md:col-span-2">
                <Label>About</Label>
                <Textarea value={form.about} onChange={(e) => update('about', e.target.value)} className="mt-1 min-h-[110px]" />
              </div>
              <div className="md:col-span-2">
                <Label>Services</Label>
                <Textarea value={form.services} onChange={(e) => update('services', e.target.value)} className="mt-1 min-h-[96px]" placeholder="Service 1, Service 2, Service 3" />
              </div>
              <div className="md:col-span-2">
                <Label>SEO Description</Label>
                <Textarea value={form.seo_description} onChange={(e) => update('seo_description', e.target.value)} className="mt-1 min-h-[96px]" />
              </div>
              <div className="md:col-span-2">
                <Label>SEO Keywords</Label>
                <Input value={form.seo_keywords} onChange={(e) => update('seo_keywords', e.target.value)} className="mt-1" placeholder="business, services, contact" />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => update('status', v)}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="disabled">Disabled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2 flex flex-wrap gap-2">
                <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white" disabled={saving}>
                  <Plus size={16} className="mr-1" /> {saving ? 'Saving...' : (profile?.id ? 'Update Profile' : 'Create Profile')}
                </Button>
                {profile?.id ? (
                  <Button type="button" variant="outline" className="border-slate-200" onClick={toggleStatus}>
                    {profile.status === 'active' ? 'Disable Profile' : 'Enable Profile'}
                  </Button>
                ) : null}
                {publicUrl ? <p className="text-sm text-slate-500 break-all">Public URL: <span className="font-mono text-slate-900">{publicUrl}</span></p> : null}
              </div>
            </form>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl overflow-hidden">
            <div className="h-40 bg-slate-100 relative">
              {(form.cover_image || profile?.cover_image) ? <img src={form.cover_image || profile?.cover_image} alt="cover" className="h-full w-full object-cover" /> : null}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent" />
              <div className="absolute left-4 bottom-4 flex items-end gap-3">
                <div className="h-16 w-16 rounded-2xl overflow-hidden bg-white border-4 border-white shadow-md">
                  {(form.logo || profile?.logo) ? <img src={form.logo || profile?.logo} alt="logo" className="h-full w-full object-cover" /> : <div className="h-full w-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-xl">{(form.business_name || 'B').charAt(0).toUpperCase()}</div>}
                </div>
                <div className="text-white">
                  <h3 className="text-xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>{form.business_name || profile?.business_name || 'Your Business'}</h3>
                  <p className="text-xs uppercase tracking-[0.18em] text-white/90">{form.username || profile?.username || 'username'}</p>
                </div>
              </div>
            </div>
            <CardContent className="p-4 space-y-4">
              <p className="text-sm text-slate-600 leading-7">{form.about || profile?.about || 'Your business description will appear here.'}</p>
              <div className="flex flex-wrap gap-2">
                {(String(form.services || '').split(',').map((item) => item.trim()).filter(Boolean)).slice(0, 6).map((service) => (
                  <span key={service} className="inline-flex items-center rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
                    {service}
                  </span>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button type="button" variant="outline" className="border-slate-200"><Phone size={14} className="mr-1" /> Call</Button>
                <Button type="button" variant="outline" className="border-slate-200"><MapPin size={14} className="mr-1" /> Maps</Button>
                <Button type="button" variant="outline" className="border-slate-200"><Store size={14} className="mr-1" /> Store</Button>
                <Button type="button" variant="outline" className="border-slate-200"><LayoutTemplate size={14} className="mr-1" /> vCard</Button>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
            <CardHeader>
              <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Quick Notes</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm text-slate-600">
              <p>Use a short username so your business link stays easy to share.</p>
              <p>Set your store, vCard and payment links for quick customer actions.</p>
              <p>Add services as comma-separated text to show them as chips on the public page.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
